import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Seo, { jsonLd } from "../components/Seo";
import { PublicNav, PublicFooter } from "../components/PublicLayout";
import { itemAPI, categoryAPI, authAPI, demandAPI, leadPricingAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import ProductCardMedia from "../components/ProductCardMedia";
import { MapPin } from "lucide-react";

/**
 * Map from the legacy `go(name)` vocabulary used by the marketing sub-pages
 * to real route paths. Each marketing page now lives at its own URL, so
 * search engines (and AI answer engines) index them independently.
 * Extra entries at the end map footer labels that don't yet have dedicated
 * pages onto the closest existing page.
 */
export const PAGE_TO_PATH = {
  Home: '/',
  Buyers: '/buyers',
  Vendors: '/vendors',
  'Bulk Sales': '/bulk-sales',
  Services: '/services',
  Pricing: '/pricing',
  Blog: '/blog',
  About: '/about',
  Contact: '/contact',
  'Vendor Signup': '/vendor-signup',
  'Recent Demands': '/recent-demands',
  Careers: '/about',
  'Partner with Us': '/contact',
  Press: '/contact',
  'Terms of Use': '/about',
  'Privacy Policy': '/about',
  'Refund Policy': '/about',
  'GST Policy': '/about',
};

const C = {
  green: '#1B5C3A', greenDark: '#0F3D26', greenLight: '#E8F5EE',
  gold: '#F5A623', goldLight: '#FFF8E7', goldDark: '#C4831A',
  cream: '#FAF8F3', charcoal: '#1A1A1A', muted: '#6B7280',
  border: '#E5E7EB', grayLight: '#F3F4F6', white: '#FFFFFF',
};
const F = { display: "'Fraunces', Georgia, serif", body: "'Plus Jakarta Sans', sans-serif" };

const capitalizeCity = (str) => {
  if (!str) return 'Other';
  return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

// Logo lives in components/PublicLayout.js now — the shared nav/footer use it.

// ── Shared UI ─────────────────────────────────────────────────────────────────
const Btn = ({ children, variant = 'primary', size = 'md', onClick, full = false, style: sx = {} }) => {
  const [h, setH] = useState(false);
  const V = {
    primary: { background: h ? C.greenDark : C.green, color: C.white, border: 'none' },
    gold: { background: h ? C.goldDark : C.gold, color: C.charcoal, border: 'none' },
    outline: { background: 'transparent', color: C.green, border: `2px solid ${C.green}`, opacity: h ? 0.75 : 1 },
    outlineW: { background: 'transparent', color: C.white, border: `2px solid rgba(255,255,255,0.5)`, opacity: h ? 0.75 : 1 },
    white: { background: h ? 'rgba(255,255,255,0.9)' : C.white, color: C.green, border: 'none' },
    ghost: { background: h ? C.greenLight : 'transparent', color: C.green, border: 'none' },
  };
  const S = { sm: { padding: '8px 18px', fontSize: 13 }, md: { padding: '12px 26px', fontSize: 14 }, lg: { padding: '15px 34px', fontSize: 16 } };
  return (
    <button onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} onClick={onClick}
      style={{ fontFamily: F.body, fontWeight: 700, borderRadius: 10, cursor: 'pointer', transition: 'all 0.18s', display: 'inline-flex', alignItems: 'center', gap: 8, width: full ? '100%' : 'auto', justifyContent: full ? 'center' : 'flex-start', ...V[variant], ...S[size], ...sx }}>
      {children}
    </button>
  );
};

const Tag = ({ children, bg = C.greenLight, color = C.green, style: sx = {} }) => (
  <span style={{ background: bg, color, padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, fontFamily: F.body, display: 'inline-block', letterSpacing: 0.3, ...sx }}>{children}</span>
);

const SLabel = ({ children, light }) => (
  <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: light ? 'rgba(255,255,255,0.55)' : C.gold, textTransform: 'uppercase', letterSpacing: 3, marginBottom: 14 }}>{children}</div>
);

const STitle = ({ children, light, center, size = 40 }) => (
  <h2 style={{ fontFamily: F.display, fontSize: size, fontWeight: 900, color: light ? C.white : C.charcoal, lineHeight: 1.1, margin: `0 0 16px`, textAlign: center ? 'center' : 'left' }}>{children}</h2>
);

const SSub = ({ children, light, center }) => (
  <p style={{ fontFamily: F.body, fontSize: 16, color: light ? 'rgba(255,255,255,0.72)' : C.muted, lineHeight: 1.72, margin: '0 0 32px', textAlign: center ? 'center' : 'left' }}>{children}</p>
);

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
      <div onClick={() => setOpen(!open)} style={{ padding: '18px 22px', display: 'flex', justifyContent: 'space-between', cursor: 'pointer', fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal, background: open ? C.greenLight : C.white }}>
        {q}<span style={{ color: C.green, fontSize: 20, fontWeight: 400 }}>{open ? '−' : '+'}</span>
      </div>
      {open && <div style={{ padding: '14px 22px', fontFamily: F.body, fontSize: 14, color: C.muted, lineHeight: 1.68, background: C.white }}>{a}</div>}
    </div>
  );
};



const inp = (extra = {}) => ({ width: '100%', boxSizing: 'border-box', padding: '12px 16px', borderRadius: 10, border: `1.5px solid ${C.border}`, fontFamily: F.body, fontSize: 14, color: C.charcoal, outline: 'none', marginBottom: 16, background: C.white, ...extra });

// Old internal NavBar / Footer were removed — PublicLayout owns the chrome.

// ── HOME PAGE — catalog-first ─────────────────────────────────────────────────
export const HomePage = ({ go }) => {
  const MODELS = [
    { icon: '📋', title: 'Demand Marketplace', tag: 'For Buyers', tagC: C.green, desc: 'Post any product or service at your budget. Verified vendors across India submit competing offers. You pick the best.', cta: 'How It Works', page: 'Buyers' },
    { icon: '📦', title: 'Bulk Sales', tag: 'For Vendors', tagC: C.goldDark, desc: 'List your inventory with a price range and stock quantity. Buyers bid within your range. Great for clearing stock or wholesale.', cta: 'Learn More', page: 'Bulk Sales' },
    { icon: '🛠️', title: 'Service Marketplace', tag: 'Both', tagC: '#7C3AED', desc: 'From GST filing to international tours — post your requirement and qualified, verified service providers send you their best quotes.', cta: 'Explore Services', page: 'Services' },
  ];

  const [tree, setTree] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [recentDemands, setRecentDemands] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingItems, setLoadingItems] = useState(true);
  const [loadingDemands, setLoadingDemands] = useState(true);

  useEffect(() => {
    categoryAPI.tree().then(r => setTree(r.data.data || [])).catch(() => { });
    itemAPI.list({ featured: 'true', limit: 12 })
      .then(r => setFeatured(r.data.data || []))
      .catch(() => { })
      .finally(() => setLoadingItems(false));

    demandAPI.publicGrouped()
      .then(r => setRecentDemands(r.data.data || []))
      .catch(() => { })
      .finally(() => setLoadingDemands(false));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    window.location.href = q ? `/catalog?search=${encodeURIComponent(q)}` : '/catalog';
  };

  const goCategory = (cat) => { window.location.href = `/c/${cat.slug || cat._id}`; };
  const goItem = (item) => { window.location.href = `/item/${item.slug || item._id}`; };

  const FAQ = [
    { q: 'How does Kanjusee work?', a: 'Kanjusee is a reverse-bidding marketplace. Buyers post what they need at the price they can afford. Verified vendors then compete by offering their best deal. The buyer reviews competing offers and picks the best one.' },
    { q: 'Is it free to post a demand?', a: 'Yes, posting a demand on Kanjusee is 100% free for buyers. Vendors pay a small lead fee only when qualifying demands reach them.' },
    { q: 'Why are prices not shown on items?', a: 'Kanjusee is not a conventional price-tag marketplace. Buyers name the price they want to pay and vendors choose whether to match it — so anchoring to a public price would defeat the point.' },
    { q: 'Can I post a demand that needs financing?', a: 'Yes. While posting a demand, tick "I want to take a loan" and enter the minimum down payment you can afford. This creates a separate financial lead that is routed only to verified banks and NBFCs — regular vendors never see it.' },
    { q: 'Who can be a vendor on Kanjusee?', a: 'Two types of vendors: "Normal" vendors selling products or services, and "Financial" vendors (banks, NBFCs, lenders). Both register for free and configure filters for the leads they want to receive.' },
  ];

  const POSTS = [
    { tag: 'Buyer Guide', title: 'How to Get the Best Price on Electronics Without Bargaining', time: '5 min' },
    { tag: 'Vendor Tips', title: 'Why Lead-Based Marketing Is Replacing Facebook Ads for Retailers in 2025', time: '7 min' },
    { tag: 'Platform', title: 'Understanding the Buyer Seriousness Score and How It Protects Vendors', time: '4 min' },
  ];

  return (
    <div>
      <Seo
        title="Name your price. Vendors compete."
        description="Kanjusee is India's buyer-powered reverse-bidding marketplace. Post any product or service demand at your budget; verified vendors compete with their best offers."
        keywords={['reverse bidding', 'name your price', 'B2B marketplace India', 'buyer-powered marketplace', 'Kanjusee']}
        jsonLd={[jsonLd.organization(), jsonLd.website(), jsonLd.faq(FAQ)]}
      />
      {/* HERO with search */}
      <section style={{ background: `linear-gradient(148deg, ${C.greenDark} 0%, ${C.green} 58%, #2D7A50 100%)`, padding: '80px 24px 60px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'rgba(245,166,35,0.1)' }} />
        <div style={{ maxWidth: 980, margin: '0 auto', textAlign: 'center' }}>
          <Tag bg="rgba(245,166,35,0.18)" color={C.gold} sx={{ marginBottom: 18, fontSize: 12 }}>🇮🇳 India's Buyer-Powered Marketplace</Tag>
          <h1 className="heading-xl" style={{ color: C.white, margin: '16px 0 18px', letterSpacing: -1 }}>
            Name your price. <span style={{ color: C.gold }}>Vendors compete.</span>
          </h1>
          <p style={{ fontFamily: F.body, fontSize: 16, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, maxWidth: 680, margin: '0 auto 28px' }}>
            Search the catalog, pick what you need, and post a demand at your budget. Verified vendors across India submit their best offers — you pick the winner.
          </p>

          {/* Big search */}
          <form onSubmit={onSearch} role="search" aria-label="Search products and services"
            style={{ maxWidth: 680, margin: '0 auto', display: 'flex', gap: 10, background: C.white, padding: 8, borderRadius: 14, boxShadow: '0 18px 40px rgba(0,0,0,0.22)', flexDirection: window.innerWidth < 480 ? 'column' : 'row' }}>
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="iPhone, GST, Tour…"
              aria-label="Search the catalog"
              style={{ flex: 1, border: 'none', outline: 'none', fontFamily: F.body, fontSize: 15, padding: '12px 14px', color: C.charcoal, background: 'transparent' }}
            />
            <button type="submit" style={{ background: C.green, color: C.white, border: 'none', borderRadius: 10, padding: '12px 22px', fontFamily: F.body, fontSize: 14, fontWeight: 700, cursor: 'pointer', width: window.innerWidth < 480 ? '100%' : 'auto' }}>
              Search →
            </button>
          </form>

          <div style={{ marginTop: 18, fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
            No price tags. No haggling. You post the price you want to pay — vendors choose whether to match it.
          </div>

          <div className="grid-1-2-4" style={{ marginTop: 50, borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 32, textAlign: 'center' }}>
            {[['12,000+', 'Active Demands'], ['4,800+', 'Verified Vendors'], ['180+', 'Cities'], ['Free', 'For Buyers']].map(([v, l], i) => (
              <div key={l} style={{ padding: '0 24px', borderRight: (i < 3 && window.innerWidth > 768) ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                <div style={{ fontFamily: F.display, fontSize: 28, fontWeight: 900, color: C.gold }}>{v}</div>
                <div style={{ fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE CATEGORIES — taxonomy from DB */}
      <section aria-labelledby="cats-heading" style={{ background: C.white, padding: '56px 24px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <SLabel>Browse by Category</SLabel>
              <h2 id="cats-heading" style={{ fontFamily: F.display, fontSize: 32, color: C.charcoal, fontWeight: 900, lineHeight: 1.1 }}>Every category. Your price.</h2>
            </div>
            <Btn variant="outline" onClick={() => window.location.href = '/catalog'}>View Full Catalog →</Btn>
          </div>
          {tree.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 14 }}>Loading categories…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
              {/* Hide zero-item categories — their button would dead-end (no item to bid on).
                  `?? 1` fails open if an older API response lacks itemCount. */}
              {tree.filter(cat => (cat.itemCount ?? 1) > 0).slice(0, 12).map(cat => (
                <a key={cat._id}
                  href={`/c/${cat.slug || cat._id}`}
                  onClick={e => { e.preventDefault(); goCategory(cat); }}
                  style={{ textDecoration: 'none', background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 14, padding: '18px 16px', transition: 'all 0.18s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.green; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 22px rgba(27,92,58,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                  {cat.icon && (cat.icon.startsWith('http') || cat.icon.startsWith('/uploads')) ? (
                    <img src={cat.icon} alt={cat.name} style={{ width: 36, height: 36, objectFit: 'contain', marginBottom: 8, display: 'block' }} />
                  ) : (
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{cat.icon || '📦'}</div>
                  )}
                  <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.charcoal, lineHeight: 1.3 }}>{cat.name}</div>
                  {cat.subcategories?.length > 0 && (
                    <div style={{ fontFamily: F.body, fontSize: 11, color: C.muted, marginTop: 6 }}>
                      {cat.subcategories.slice(0, 3).map(s => s.name).join(' · ')}{cat.subcategories.length > 3 ? '…' : ''}
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FEATURED ITEMS — live from DB, no prices */}
      <section aria-labelledby="featured-heading" style={{ background: C.cream, padding: '56px 24px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <SLabel>Trending</SLabel>
              <h2 id="featured-heading" style={{ fontFamily: F.display, fontSize: 32, color: C.charcoal, fontWeight: 900, lineHeight: 1.1 }}>Popular items people are bidding on</h2>
            </div>
            <Btn variant="outline" onClick={() => window.location.href = '/catalog'}>See All →</Btn>
          </div>

          {loadingItems ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 14 }}>Loading items…</div>
          ) : featured.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 14 }}>No featured items yet — check the full catalog.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 18 }}>
              {featured.map(item => (
                <article key={item._id}
                  onClick={() => goItem(item)}
                  style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', transition: 'all 0.18s', cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.green; e.currentTarget.style.boxShadow = '0 10px 22px rgba(27,92,58,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}>
                  <div style={{ padding: '14px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {item.categoryId?.name && <Tag sx={{ fontSize: 10 }}>{item.categoryId.name}</Tag>}
                    {item.subCategoryId?.name && <Tag bg={C.goldLight} color={C.goldDark} sx={{ fontSize: 10 }}>{item.subCategoryId.name}</Tag>}
                    {item.isFeatured && <Tag bg={C.goldLight} color={C.goldDark} sx={{ fontSize: 10 }}>⭐ Featured</Tag>}
                  </div>
                  {/* Product image */}
                  <ProductCardMedia item={item} height={160} />

                  <div style={{ padding: '6px 16px 16px' }}>
                    {item.brandId?.name && <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.5, marginBottom: 4 }}>{item.brandId.name}</div>}
                    <h3 style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal, lineHeight: 1.35, marginBottom: 12, minHeight: 40 }}>{item.name}</h3>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <a href={`/item/${item.slug || item._id}`}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); goItem(item); }}
                        style={{ flex: 1, textAlign: 'center', background: 'transparent', color: C.green, border: `1.5px solid ${C.green}`, borderRadius: 9, padding: '9px 12px', fontFamily: F.body, fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                        Details
                      </a>
                      <button onClick={e => { e.stopPropagation(); window.location.href = `/post-demand?itemId=${item._id}`; }}
                        style={{ flex: 1, background: C.gold, color: C.charcoal, border: 'none', borderRadius: 9, padding: '9px 12px', fontFamily: F.body, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Name Price →
                      </button>
                    </div>
                    {item.demandCount > 0 && (
                      <div style={{ fontFamily: F.body, fontSize: 11, color: C.muted, marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                        🔥 {item.demandCount} demand{item.demandCount > 1 ? 's' : ''} posted
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* RECENT DEMANDS SECTION */}
      <section aria-labelledby="recent-demands-heading" style={{ background: C.white, padding: '56px 24px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <SLabel>Market Pulse</SLabel>
              <h2 id="recent-demands-heading" style={{ fontFamily: F.display, fontSize: 32, color: C.charcoal, fontWeight: 900, lineHeight: 1.1 }}>What buyers are asking for right now</h2>
              <p style={{ fontFamily: F.body, fontSize: 14, color: C.muted, marginTop: 8 }}>Real-time requirements from across India. Vendors are already bidding!</p>
            </div>
            <Btn variant="outline" onClick={() => window.location.href = '/recent-demands'}>See All Recent Demands →</Btn>
          </div>

          {loadingDemands ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 14 }}>Loading recent demands…</div>
          ) : recentDemands.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 14 }}>No public demands yet. Be the first to post!</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
              {recentDemands.slice(0, 3).map(cityData => (
                <div key={cityData._id} style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 18, padding: 24, transition: 'all 0.18s', position: 'relative' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.green; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(27,92,58,0.08)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MapPin size={18} color={C.green} style={{ flexShrink: 0 }} />
                      <h3 style={{ fontFamily: F.display, fontSize: 18, fontWeight: 900, color: C.charcoal, margin: 0 }}>
                        {capitalizeCity(cityData._id)}
                      </h3>
                    </div>
                    <div style={{ background: C.greenLight, color: C.green, padding: '4px 10px', borderRadius: 8, fontSize: 10, fontWeight: 800 }}>
                      {cityData.totalCount} Demand{cityData.totalCount > 1 ? 's' : ''}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: 8, minHeight: 90 }}>
                    {cityData.categories.map((cat, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: C.grayLight, borderRadius: 8 }}>
                        <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.charcoal }}>
                          {cat.name}
                        </span>
                        <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 800, color: C.green }}>
                          {cat.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* THREE MODELS */}
      <div style={{ background: C.white, padding: '64px 24px' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <SLabel center>Three Ways to Trade</SLabel>
            <STitle center>One Platform. Every Transaction.</STitle>
            <SSub center>Whether you're buying, selling in bulk, or booking a service — Kanjusee has the right market for you.</SSub>
          </div>
          <div className="grid-1-2-3">
            {MODELS.map(m => (
              <div key={m.title} style={{ background: C.white, borderRadius: 22, padding: 34, border: `1.5px solid ${C.border}`, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ position: 'absolute', top: -24, right: -24, fontSize: 100, opacity: 0.05 }}>{m.icon}</div>
                <div style={{ fontSize: 42, marginBottom: 18 }}>{m.icon}</div>
                <Tag bg={m.tagC + '1A'} color={m.tagC} sx={{ marginBottom: 14 }}>{m.tag}</Tag>
                <h3 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 700, color: C.charcoal, margin: '10px 0 12px' }}>{m.title}</h3>
                <p style={{ fontFamily: F.body, fontSize: 14, color: C.muted, lineHeight: 1.68, flex: 1 }}>{m.desc}</p>
                <div onClick={() => go(m.page)} style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.green, marginTop: 20, cursor: 'pointer' }}>{m.cta} →</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div style={{ background: C.white, padding: '88px 24px' }}>
        <div className="container">
          <div className="grid-1-2" style={{ gap: 72, alignItems: 'center' }}>
            <div>
              <SLabel>For Buyers</SLabel>
              <STitle>Getting the Best Price<br />Has Never Been This Easy</STitle>
              <SSub>Stop browsing 10 websites and calling 5 dealers. Post once — verified vendors fight for your order.</SSub>
              <div style={{ display: 'grid', gap: 28 }}>
                {[
                  ['📋', '01', 'Post Your Demand', 'Tell us what you want and the price you\'re willing to pay. Takes 60 seconds. Always free.'],
                  ['📬', '02', 'Vendors Respond', 'Verified sellers from across India receive your demand and submit their best competing offers.'],
                  ['✅', '03', 'You Pick & Save', 'Compare price, rating, delivery time, and warranty side by side. Accept the best deal. Done.'],
                ].map(([icon, n, t, d]) => (
                  <div key={n} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{ fontFamily: F.display, fontSize: 48, fontWeight: 900, color: C.greenLight, lineHeight: 1 }}>{n}</div>
                      <div style={{ position: 'absolute', bottom: 2, left: 4, fontSize: 22 }}>{icon}</div>
                    </div>
                    <div style={{ paddingTop: 8 }}>
                      <div style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal, marginBottom: 6 }}>{t}</div>
                      <div style={{ fontFamily: F.body, fontSize: 13, color: C.muted, lineHeight: 1.65 }}>{d}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 36 }}>
                <Btn size="lg" onClick={() => go('Buyers')}>Learn More for Buyers →</Btn>
              </div>
            </div>
            <div>
              {/* Reverse-bidding explainer — NO prices shown */}
              <div style={{ background: C.cream, borderRadius: 20, padding: 28 }}>
                <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 20 }}>How a demand works</div>
                <div style={{ background: C.white, borderRadius: 16, padding: 22, border: `1.5px solid ${C.border}`, marginBottom: 14 }}>
                  <Tag sx={{ marginBottom: 10 }}>Example — Electronics › Mobile › Apple</Tag>
                  <div style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal }}>iPhone 16 Pro — 256 GB Natural</div>
                  <div style={{ fontFamily: F.body, fontSize: 12, color: C.muted, marginTop: 5 }}>📍 Surat · Qty: 1</div>
                  <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 14, paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontFamily: F.body, fontSize: 12, color: C.muted }}>Your price</div>
                      <div style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.green }}>🔒 Sealed — only vendors see it</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: F.display, fontSize: 22, fontWeight: 700, color: C.gold }}>7</div>
                      <div style={{ fontFamily: F.body, fontSize: 11, color: C.muted }}>vendor offers</div>
                    </div>
                  </div>
                </div>
                <div style={{ background: C.goldLight, borderRadius: 12, padding: '12px 16px', fontFamily: F.body, fontSize: 13, color: C.goldDark, lineHeight: 1.55 }}>
                  Only you see each vendor's offer. Pick the one that suits delivery, rating, warranty — and price.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* VENDOR PITCH */}
      <div style={{ background: `linear-gradient(140deg, ${C.greenDark} 0%, ${C.green} 100%)`, padding: '88px 24px' }}>
        <div className="container">
          <div className="grid-1-2" style={{ gap: 64, alignItems: 'center' }}>
            <div>
              <SLabel light>For Vendors</SLabel>
              <STitle light>Stop Wasting Money<br />on Cold Outreach.</STitle>
              <SSub light>Set your exact category, brand, and buyer price range. Only matching leads reach you. Every rupee goes to a buyer who actually wants what you sell.</SSub>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 36 }}>
                {[['🎯', 'Precision filters — Brand + Price'], ['💰', 'Pay only per matching lead'], ['📊', 'Real market demand data'], ['🔁', '48hr repeat lead protection']].map(([i, t]) => (
                  <div key={t} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 18 }}>{i}</span>
                    <span style={{ fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 }}>{t}</span>
                  </div>
                ))}
              </div>
              <Btn size="lg" variant="gold" onClick={() => go('Vendor Signup')}>Start Receiving Leads →</Btn>
            </div>
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 18, padding: 24, border: '1.5px solid rgba(255,255,255,0.12)' }}>
                <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 14 }}>Your Lead Filter</div>
                {[['Category', 'Electronics → Mobile → Apple'], ['Buyer Budget', '₹60,000 – ₹1,40,000'], ['City', 'Surat (full rate)'], ['Extended', 'Gujarat (50% lead cost)']].map(([k, v]) => (
                  <div key={k} style={{ padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>{k}</span>
                    <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 600, color: C.white }}>{v}</span>
                  </div>
                ))}
                <div style={{ marginTop: 16, padding: '14px 18px', background: 'rgba(245,166,35,0.15)', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontFamily: F.body, fontSize: 13, color: C.gold }}>Leads this week</span>
                  <span style={{ fontFamily: F.display, fontSize: 26, fontWeight: 700, color: C.gold }}>23</span>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 18, padding: 22, border: '1.5px solid rgba(255,255,255,0.12)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16 }}>
                {[['₹30', 'per Apple lead'], ['₹15', 'out-of-city (50%)'], ['48hrs', 'repeat protection'], ['₹500', 'min wallet']].map(([v, l]) => (
                  <div key={l}>
                    <div style={{ fontFamily: F.display, fontSize: 22, fontWeight: 700, color: C.gold }}>{v}</div>
                    <div style={{ fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ANALYTICS TEASER */}
      <div className="analytics-teaser-sec">
        <div className="analytics-teaser-grid">
          <div className="analytics-teaser-card">
            <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 22 }}>Market Intelligence — Surat, Gujarat</div>
            {[['Apple Mobile', 2400, 340, C.green], ['Samsung Mobile', 1800, 520, '#2563EB'], ['Dell Laptops', 640, 110, C.goldDark]].map(([cat, total, resp, color]) => (
              <div key={cat} style={{ marginBottom: 22 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px 8px', marginBottom: 7 }}>
                  <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.charcoal }}>{cat}</span>
                  <span style={{ fontFamily: F.body, fontSize: 12, color: C.muted }}>{total.toLocaleString()} quarterly demands</span>
                </div>
                <div style={{ background: C.border, borderRadius: 6, height: 8 }}>
                  <div style={{ background: color, height: 8, borderRadius: 6, width: `${(resp / total) * 100}%`, transition: 'width 1s' }} />
                </div>
                <div style={{ fontFamily: F.body, fontSize: 11, color: C.muted, marginTop: 4 }}>{resp} vendors responded in your city</div>
              </div>
            ))}
            <div style={{ background: C.greenLight, borderRadius: 12, padding: '12px 16px', marginTop: 8 }}>
              <div style={{ fontFamily: F.body, fontSize: 12, color: C.green, fontWeight: 600 }}>📈 Samsung demand up 34% in your city this quarter. Consider expanding your catalogue.</div>
            </div>
          </div>
          <div>
            <SLabel>Vendor Intelligence</SLabel>
            <STitle>Know Your Market<br />Before You Stock It.</STitle>
            <SSub>See real buyer demand data for every category — even categories you don't operate in yet. Make smarter purchase decisions based on actual intent, not guesswork.</SSub>
            <div style={{ display: 'grid', gap: 14, marginBottom: 32 }}>
              {[['📊', 'Layer 1: Your performance — leads, offers, spend by brand/city/period'], ['🌐', 'Layer 2: Market intel — anonymous demand volume, competitor response rates'], ['💡', 'Stocking insights — "2,400 Apple leads in Surat Q1. You captured 340."']].map(([i, t]) => (
                <div key={t} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 20 }}>{i}</span>
                  <span style={{ fontFamily: F.body, fontSize: 14, color: C.charcoal, lineHeight: 1.55 }}>{t}</span>
                </div>
              ))}
            </div>
            <Btn onClick={() => go('Vendors')}>See Full Analytics →</Btn>
          </div>
        </div>
      </div>

      {/* BLOG PREVIEW */}
      <div style={{ background: C.cream, padding: '88px 24px' }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 36, flexWrap: 'wrap', gap: 16 }}>
            <div>
              <SLabel center={window.innerWidth < 768}>From the Blog</SLabel>
              <h2 className="heading-md" style={{ color: C.charcoal, textAlign: window.innerWidth < 768 ? 'center' : 'left' }}>Tips, Guides & Market Insights</h2>
            </div>
            <Btn variant="outline" onClick={() => go('Blog')} sx={{ margin: window.innerWidth < 768 ? '0 auto' : '0' }}>All Posts →</Btn>
          </div>
          <div className="grid-1-2-3">
            {POSTS.map(p => (
              <div key={p.title} onClick={() => go('Blog')} style={{ background: C.white, borderRadius: 18, padding: 26, cursor: 'pointer', border: `1.5px solid ${C.border}` }}>
                <Tag sx={{ marginBottom: 16 }}>{p.tag}</Tag>
                <h3 style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal, lineHeight: 1.55, marginBottom: 14 }}>{p.title}</h3>
                <div style={{ fontFamily: F.body, fontSize: 12, color: C.muted }}>⏱ {p.time} read</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ — AEO gold, mirrors JSON-LD */}
      <section aria-labelledby="faq-heading" style={{ background: C.white, padding: '72px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <SLabel center>Questions</SLabel>
            <h2 id="faq-heading" style={{ fontFamily: F.display, fontSize: 34, color: C.charcoal, fontWeight: 900, lineHeight: 1.15 }}>Frequently asked</h2>
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            {FAQ.map(({ q, a }) => <FaqItem key={q} q={q} a={a} />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <div style={{ background: C.goldLight, padding: '88px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 580, margin: '0 auto' }}>
          <SLabel center>Get Started Today</SLabel>
          <h2 style={{ fontFamily: F.display, fontSize: 48, fontWeight: 900, color: C.charcoal, lineHeight: 1.1, marginBottom: 16 }}>
            Kyunki Har Ek<br /><span style={{ color: C.green }}>Paisa Zaruri Hai.</span>
          </h2>
          <p style={{ fontFamily: F.body, fontSize: 16, color: C.muted, lineHeight: 1.72, marginBottom: 38 }}>
            Join 12,000+ buyers who've already saved money. Or register as a vendor and start receiving pre-qualified leads today.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Btn size="lg" onClick={() => go('Buyers')}>I'm a Buyer — Post Free</Btn>
            <Btn size="lg" variant="outline" onClick={() => go('Vendor Signup')}>I'm a Vendor — Join Now</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── BUYERS PAGE ───────────────────────────────────────────────────────────────
export const BuyersPage = ({ go }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handlePostDemand = () => {
    if (user && user.role === 'buyer') {
      navigate('/catalog');
    } else {
      navigate('/login?next=/catalog');
    }
  };

  return (
    <div>
      <div style={{ background: `linear-gradient(140deg, ${C.greenDark}, ${C.green})`, padding: '88px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <SLabel light center>For Buyers</SLabel>
          <h1 style={{ fontFamily: F.display, fontSize: 54, fontWeight: 900, color: C.white, lineHeight: 1.08, margin: '14px 0 22px' }}>
            Post Once.<br /><span style={{ color: C.gold }}>Get Offers From<br />Across India.</span>
          </h1>
          <p style={{ fontFamily: F.body, fontSize: 17, color: 'rgba(255,255,255,0.78)', lineHeight: 1.72, marginBottom: 38 }}>
            No more browsing, calling dealers, or accepting inflated prices. You name your budget. Verified vendors fight to win your business.
          </p>
          <Btn size="lg" variant="gold" onClick={handlePostDemand}>Post Your First Demand — Free →</Btn>
        </div>
      </div>
      <div style={{ background: C.cream, padding: '88px 24px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <SLabel center>Why Buyers Love Kanjusee</SLabel>
            <STitle center>The Power Is Yours</STitle>
          </div>
          <div className="grid-1-2-3" style={{ marginBottom: 72 }}>
            {[['🆓', 'Always Free', 'Posting a demand costs zero. You never pay for leads or offers. Vendors pay to reach you.'], ['🎯', 'Set Your Own Price', 'Post at the budget you actually have. Vendors either match it or they don\'t.'], ['🌍', '180+ Cities', 'Offers from your city or anywhere in India. Wider choice, better prices.'], ['⭐', 'Verified Only', 'Every vendor is verified. You see ratings, reviews, and response history.'], ['📊', 'Compare Side-by-Side', 'Compare price, delivery, warranty, and seller rating in one clean view.'], ['🔒', 'Zero Obligation', 'Receiving offers doesn\'t mean you must buy. No spam. No pressure.']].map(([i, t, d]) => (
              <div key={t} style={{ background: C.white, borderRadius: 18, padding: 28, border: `1.5px solid ${C.border}` }}>
                <div style={{ fontSize: 36, marginBottom: 14 }}>{i}</div>
                <h3 style={{ fontFamily: F.body, fontSize: 16, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>{t}</h3>
                <p style={{ fontFamily: F.body, fontSize: 13, color: C.muted, lineHeight: 1.65 }}>{d}</p>
              </div>
            ))}
          </div>
          <div className="what-you-can-post-container">
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
              <SLabel light center>What You Can Post</SLabel>
              <h2 className="what-you-can-post-title">Products, Services, <span style={{ color: C.gold }}>Anything.</span></h2>
            </div>
            <div className="what-you-can-post-grid">
              {[['📱', 'Mobile Phones', 'iPhone 16, Samsung S24, OnePlus 12…'], ['💻', 'Laptops', 'MacBook, Dell XPS, HP Spectre…'], ['✈️', 'International Tours', 'Europe, Dubai, USA, Singapore packages'], ['📊', 'GST & Accounting', 'Filing, ITR, audit, company registration'], ['🏥', 'Medical Treatment', 'Orthopaedic, dental, eye, diagnostics'], ['🏠', 'Home Interiors', 'Modular kitchen, full home, renovation'], ['⚖️', 'Legal Services', 'Property docs, contracts, IP, corporate'], ['🚗', 'Automobiles', 'New car, used car, 2-wheelers, EVs']].map(([e, t, d]) => (
                <div key={t} style={{ display: 'flex', gap: 14, padding: 18, background: 'rgba(255,255,255,0.08)', borderRadius: 14 }}>
                  <span style={{ fontSize: 26 }}>{e}</span>
                  <div>
                    <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.white }}>{t}</div>
                    <div style={{ fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 3 }}>{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 60 }}>
            <SLabel center>Buyer FAQs</SLabel>
            <STitle center>Common Questions</STitle>
            <div style={{ maxWidth: 720, margin: '32px auto 0', display: 'grid', gap: 14 }}>
              {[['Is posting really free?', 'Yes, 100% free for buyers always. Vendors pay a small lead fee to receive your demand — you pay nothing.'], ['What if no vendor matches my price?', 'Your demand stays active for its full duration. You can also update the price or repost anytime at no cost.'], ['How do I know vendors are genuine?', 'Every vendor is verified by Kanjusee before activation. You see their rating, deal history, and response rate on every offer.'], ['Can I get offers from other cities?', 'Yes. Enable nationwide leads when posting. Out-of-city vendors often offer better prices since they pay lower lead costs.']].map(([q, a]) => (
                <FaqItem key={q} q={q} a={a} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── VENDORS PAGE ──────────────────────────────────────────────────────────────
export const VendorsPage = ({ go }) => (
  <div>
    <div style={{ background: `linear-gradient(145deg, #111827, #1f2937)`, padding: '88px 24px' }}>
      <div className="container">
        <div className="grid-1-2" style={{ gap: 64, alignItems: 'center' }}>
          <div>
            <SLabel light>For Vendors</SLabel>
            <h1 className="heading-xl" style={{ color: C.white, margin: '14px 0 22px' }}>
              Only Pay For<br /><span style={{ color: C.gold }}>Leads That<br />Match Your Business.</span>
            </h1>
            <p style={{ fontFamily: F.body, fontSize: 16, color: 'rgba(255,255,255,0.68)', lineHeight: 1.72, marginBottom: 36 }}>
              Configure your exact category, brand, and buyer price range. Zero noise. Every lead delivered is a pre-qualified buyer who can actually afford what you sell.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Btn size="lg" variant="gold" onClick={() => go('Vendor Signup')}>Register Free →</Btn>
              <Btn size="lg" variant="outlineW" onClick={() => go('Pricing')}>See Pricing</Btn>
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 20, padding: 28, border: '1.5px solid rgba(255,255,255,0.09)' }}>
            <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 20 }}>Your Filter Config — TechZone Surat</div>
            {[['Category', 'Electronics → Mobile Phones → Apple'], ['Buyer Budget', '₹60,000 – ₹1,40,000'], ['Home City', 'Surat (full lead cost)'], ['Extended', 'All Gujarat (50% lead cost)'], ['Daily Cap', '₹1,000 / day'], ['Monthly Cap', '₹18,000 / month']].map(([k, v]) => (
              <div key={k} style={{ padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{k}</span>
                <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 600, color: C.white }}>{v}</span>
              </div>
            ))}
            <div style={{ marginTop: 18, padding: '14px 18px', background: 'rgba(245,166,35,0.12)', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: F.body, fontSize: 13, color: C.gold }}>Active leads this week</span>
              <span style={{ fontFamily: F.display, fontSize: 28, fontWeight: 700, color: C.gold }}>23</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div style={{ background: C.cream, padding: '88px 24px' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <SLabel center>Platform Features</SLabel>
          <STitle center>Everything a Serious Vendor Needs</STitle>
        </div>
        <div className="grid-1-2" style={{ gap: 22, marginBottom: 52 }}>
          {[['🎯', 'Precision Lead Filtering', 'Set Sub-category + Brand + buyer price range. A ₹12,000 Redmi buyer never reaches an Apple vendor. Zero wasted spend.'], ['📊', 'Two-Layer Market Analytics', 'Layer 1: your own performance data. Layer 2: anonymous market demand — even for categories you don\'t operate in.'], ['🔁', '48-Hour Repeat Protection', 'If a buyer reposts the same demand type within 48hrs, you get an alert at zero cost. Never pay twice for the same buyer.'], ['💰', 'Wallet-Based Billing', 'Preload ₹500+. Leads auto-deducted. Monthly GST invoice emailed + downloadable. No refunds, no surprises.'], ['📦', 'Bulk Sales Listings', 'Post SKUs with price range and stock qty. Buyers bid within your range. Pay only per bid received.'], ['😐', 'Buyer Seriousness Score', 'See every buyer\'s response rate and deal-close history before deciding to engage. Filter time-wasters without blocking anyone.']].map(([i, t, d]) => (
            <div key={t} style={{ background: C.white, borderRadius: 18, padding: 28, border: `1.5px solid ${C.border}`, display: 'flex', gap: 20 }}>
              <div style={{ fontSize: 34, flexShrink: 0 }}>{i}</div>
              <div>
                <h3 style={{ fontFamily: F.body, fontSize: 16, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>{t}</h3>
                <p style={{ fontFamily: F.body, fontSize: 13, color: C.muted, lineHeight: 1.65 }}>{d}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'center' }}>
          <Btn size="lg" variant="gold" onClick={() => go('Vendor Signup')}>Register as a Vendor →</Btn>
        </div>
      </div>
    </div>
  </div>
);

// ── PRICING PAGE ──────────────────────────────────────────────────────────────
const DEFAULT_RATES = [
  { category: 'Electronics', subCategory: 'Mobile Phones', brand: 'Apple', localRate: '₹30', outCityRate: '₹15', leadReceivingFrom: 'home_state' },
  { category: 'Electronics', subCategory: 'Mobile Phones', brand: 'Samsung / Others', localRate: '₹18', outCityRate: '₹9', leadReceivingFrom: 'home_state' },
  { category: 'Electronics', subCategory: 'Laptops', brand: 'Apple', localRate: '₹28', outCityRate: '₹14', leadReceivingFrom: 'home_state' },
  { category: 'Electronics', subCategory: 'Laptops', brand: 'Dell / HP / Others', localRate: '₹22', outCityRate: '₹11', leadReceivingFrom: 'home_state' },
  { category: 'Travel', subCategory: 'International Tours', brand: 'All Providers', localRate: '₹35', outCityRate: '₹17.50', leadReceivingFrom: 'home_state' },
  { category: 'Travel', subCategory: 'Domestic Tours', brand: 'All Providers', localRate: '₹20', outCityRate: '₹10', leadReceivingFrom: 'home_state' },
  { category: 'Finance', subCategory: 'Accounting', brand: 'GST / ITR Filing', localRate: '₹22', outCityRate: '₹11', leadReceivingFrom: 'home_state' },
  { category: 'Healthcare', subCategory: 'Medical Treatment', brand: 'All Providers', localRate: '₹30', outCityRate: '₹15', leadReceivingFrom: 'home_state' },
  { category: 'Home', subCategory: 'Interior Design', brand: 'All Providers', localRate: '₹25', outCityRate: '₹12.50', leadReceivingFrom: 'home_state' },
  { category: 'Fashion', subCategory: 'Footwear', brand: 'Nike / Adidas / Others', localRate: '₹15', outCityRate: '₹7.50', leadReceivingFrom: 'home_state' },
  { category: 'Gaming', subCategory: 'Consoles', brand: 'Sony / Microsoft', localRate: '₹20', outCityRate: '₹10', leadReceivingFrom: 'home_state' },
  { category: 'Automobiles', subCategory: '2-Wheelers', brand: 'Honda / TVS / Hero', localRate: '₹18', outCityRate: '₹9', leadReceivingFrom: 'home_state' }
];

export const PricingPage = ({ go }) => {
  const [rows, setRows] = useState(DEFAULT_RATES);
  const [live, setLive] = useState(false);
  useEffect(() => {
    let alive = true;
    leadPricingAPI.publicList()
      .then(res => {
        const d = (res && res.data && res.data.data) || [];
        if (alive && d.length) {
          setRows(d.map(r => ({
            category: r.category,
            subCategory: r.subCategory || '—',
            brand: r.brand || 'All',
            localRate: `₹${r.localRate}`,
            outCityRate: `₹${r.outCityRate}`,
            leadReceivingFrom: r.leadReceivingFrom || 'home_state'
          })));
          setLive(true);
        }
      })
      .catch(() => { });
    return () => { alive = false; };
  }, []);
  return (
    <div>
      <div style={{ background: C.cream, padding: '88px 24px', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: 720 }}>
          <SLabel center>Transparent Pricing</SLabel>
          <h1 className="heading-xl" style={{ color: C.charcoal, margin: '14px 0 18px' }}>
            Pay Only For<br /><span style={{ color: C.green }}>Matching Leads.</span>
          </h1>
          <p style={{ fontFamily: F.body, fontSize: 16, color: C.muted, lineHeight: 1.72, marginBottom: 22 }}>Buyers always pay ₹0. Vendors pay a small fee only when a qualifying demand is delivered to them.</p>
          <Tag bg={C.goldLight} color={C.goldDark} sx={{ fontSize: 13, padding: '6px 16px' }}>Out-of-city vendor = 50% of listed rate · No exceptions</Tag>
        </div>
      </div>
      <div style={{ background: C.white, padding: '60px 24px' }}>
        <div className="container" style={{ maxWidth: 920 }}>
          <h2 style={{ fontFamily: F.display, fontSize: 28, color: C.charcoal, marginBottom: 8 }}>Lead Cost by Category</h2>
          <p style={{ fontFamily: F.body, fontSize: 14, color: C.muted, marginBottom: 28 }}>Admin-configured. Most specific taxonomy node always wins (Brand overrides Sub-category, Sub-category overrides Category).</p>
          <div style={{ display: 'grid', gap: 10, overflowX: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 1.6fr 1fr 1fr', gap: 12, minWidth: 600, padding: '10px 18px', fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 1 }}>
              <span>Category</span><span>Sub-Category</span><span>Brand</span><span style={{ textAlign: 'center' }}>Local/Flat</span><span style={{ textAlign: 'center' }}>Out-City</span>
            </div>
            {rows.map((row, idx) => {
              const isFlat = row.leadReceivingFrom === 'home_city' || row.leadReceivingFrom === 'nation_wide';
              return (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.4fr 1.6fr 1fr 1fr', gap: 12, minWidth: 600, padding: '14px 18px', background: C.cream, borderRadius: 10, alignItems: 'center' }}>
                  <Tag>{row.category}</Tag>
                  <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 600, color: C.charcoal }}>{row.subCategory}</span>
                  <span style={{ fontFamily: F.body, fontSize: 13, color: C.muted }}>{row.brand}</span>
                  <div style={{ textAlign: 'center' }}><div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, color: C.green }}>{row.localRate}</div></div>
                  <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                    {isFlat ? (
                      <span style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: C.muted,
                        background: '#e5e7eb',
                        padding: '4px 10px',
                        borderRadius: 6,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      }}>
                        {row.leadReceivingFrom === 'nation_wide' ? 'Nationwide' : 'Home City'}
                      </span>
                    ) : (
                      <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, color: C.goldDark }}>{row.outCityRate}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 16, padding: '14px 20px', background: C.greenLight, borderRadius: 10, fontFamily: F.body, fontSize: 13, color: C.green }}>
            {live
              ? '✓ Live rates — pulled directly from the admin pricing console. Most specific node (Brand › Sub-category › Category) wins; out-of-city = 50% of local unless set.'
              : '💡 Prices above are indicative. Admin sets exact rates at any taxonomy node. Most specific node takes priority.'}
          </div>

          {/* Wallet */}
          <div style={{ marginTop: 64 }}>
            <h2 style={{ fontFamily: F.display, fontSize: 28, color: C.charcoal, marginBottom: 8 }}>Wallet System</h2>
            <p style={{ fontFamily: F.body, fontSize: 14, color: C.muted, marginBottom: 28 }}>Pre-fund your wallet. Leads deducted instantly on delivery. Pause and resume anytime.</p>
            <div className="grid-1-2-4">
              {[['₹500', 'Minimum Recharge', 'Via Razorpay or Paytm'], ['₹0 = Pause', 'Lead Delivery Stops', 'No retroactive delivery for gap period'], ['SMS + App', 'Low Balance Alerts', 'At ₹500 and ₹200 remaining'], ['No Refunds', 'Once Charged = Final', 'Clean, simple policy']].map(([v, t, d]) => (
                <div key={t} style={{ background: C.cream, borderRadius: 16, padding: 22, border: `1.5px solid ${C.border}`, textAlign: 'center' }}>
                  <div style={{ fontFamily: F.display, fontSize: 22, fontWeight: 700, color: C.green, marginBottom: 8 }}>{v}</div>
                  <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 6 }}>{t}</div>
                  <div style={{ fontFamily: F.body, fontSize: 12, color: C.muted }}>{d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* GST */}
          <div style={{ marginTop: 48, background: C.goldLight, borderRadius: 18, padding: 32 }}>
            <h3 style={{ fontFamily: F.display, fontSize: 22, color: C.charcoal, marginBottom: 20 }}>🧾 GST on Invoices</h3>
            <div className="grid-1-2" style={{ gap: 24, fontFamily: F.body, fontSize: 14, color: C.charcoal, lineHeight: 1.65 }}>
              <div><strong>Gujarat Vendors (Intra-state):</strong><br />CGST 9% + SGST 9% = 18%</div>
              <div><strong>Other State Vendors (Inter-state):</strong><br />IGST 18%</div>
              <div><strong>With GSTIN (optional):</strong><br />B2B invoice — input credit eligible</div>
              <div><strong>Without GSTIN:</strong><br />B2C invoice — GST collected, no input credit</div>
            </div>
            <div style={{ marginTop: 18, fontFamily: F.body, fontSize: 12, color: C.muted }}>SAC Code: 998361 · Kanjusee registered in Gujarat · Tax invoice issued automatically on every wallet recharge (GST-inclusive, prepaid, non-refundable)</div>
          </div>
          <div style={{ marginTop: 40, textAlign: 'center' }}>
            <Btn size="lg" variant="gold" onClick={() => go('Vendor Signup')}>Start with ₹500 →</Btn>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── BULK SALES PAGE ───────────────────────────────────────────────────────────
export const BulkSalesPage = ({ go }) => (
  <div>
    <div style={{ background: `linear-gradient(140deg, #111827, #1E3A5F)`, padding: '88px 24px', textAlign: 'center' }}>
      <div className="container" style={{ maxWidth: 680 }}>
        <SLabel light center>Bulk Sales</SLabel>
        <h1 className="heading-xl" style={{ color: C.white, margin: '14px 0 22px' }}>
          List Your Stock.<br /><span style={{ color: C.gold }}>Let Buyers Bid.</span>
        </h1>
        <p style={{ fontFamily: F.body, fontSize: 17, color: 'rgba(255,255,255,0.72)', lineHeight: 1.72, marginBottom: 38 }}>
          Set a price floor and ceiling, list your inventory quantity, and let buyers compete to buy from you. Perfect for wholesale, clearance, or flash sales.
        </p>
        <Btn size="lg" variant="gold" onClick={() => go('Vendor Signup')}>List Your SKU →</Btn>
      </div>
    </div>
    <div style={{ background: C.cream, padding: '88px 24px' }}>
      <div className="container">
        <div className="grid-1-2" style={{ gap: 64, alignItems: 'start' }}>
          <div>
            <SLabel>How It Works</SLabel>
            <div style={{ display: 'grid', gap: 28 }}>
              {[['1', 'Set Your SKU', 'List your product under the right taxonomy node. Set a price floor, ceiling, total stock qty, and max units per buyer.'], ['2', 'Buyers Bid', 'Any buyer on Kanjusee sees your listing and places a bid within your configured price range.'], ['3', 'You Get a Lead', 'Every bid = one paid lead. Your wallet is charged the bulk-sales lead rate per bid received — regardless of whether you accept.'], ['4', 'You Choose Bids', 'Review all bids and accept the ones that work for you. Listing auto-closes when stock hits zero.']].map(([n, t, d]) => (
                <div key={n} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: C.green, color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</div>
                  <div>
                    <div style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal, marginBottom: 6 }}>{t}</div>
                    <div style={{ fontFamily: F.body, fontSize: 13, color: C.muted, lineHeight: 1.65 }}>{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{ background: C.white, borderRadius: 20, padding: 28, border: `1.5px solid ${C.border}`, marginBottom: 18 }}>
              <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 20 }}>Sample SKU Listing</div>
              {[['Product', 'boAt Rockerz 450 Bluetooth'], ['Category', 'Electronics → Headphones → boAt'], ['Price Range', '₹1,800 – ₹2,200 per unit'], ['Stock', '150 units available'], ['Max / Buyer', '5 units'], ['Lead Cost / Bid', '₹8 (admin bulk rate)']].map(([k, v]) => (
                <div key={k} style={{ padding: '11px 0', borderBottom: `1px solid ${C.border}` }}>
                  <div style={{ fontFamily: F.body, fontSize: 11, color: C.muted }}>{k}</div>
                  <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 600, color: C.charcoal, marginTop: 3 }}>{v}</div>
                </div>
              ))}
              <div style={{ marginTop: 18, padding: '14px 18px', background: C.greenLight, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: F.body, fontSize: 13, color: C.green, fontWeight: 600 }}>Bids received today</span>
                <span style={{ fontFamily: F.display, fontSize: 28, fontWeight: 700, color: C.green }}>18</span>
              </div>
            </div>
            <div style={{ background: C.goldLight, borderRadius: 14, padding: '18px 22px', border: `1.5px solid ${C.border}` }}>
              <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.goldDark, marginBottom: 8 }}>📦 Key Difference from Demand Marketplace</div>
              <p style={{ fontFamily: F.body, fontSize: 13, color: C.charcoal, lineHeight: 1.65 }}>In the Demand Marketplace, buyers post their requirement. In Bulk Sales, <strong>you post your stock</strong> and buyers bid to buy from you. You control price, quantity, and who you sell to.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── SERVICES PAGE ─────────────────────────────────────────────────────────────
export const ServicesPage = ({ go }) => (
  <div>
    <div style={{ background: `linear-gradient(140deg, #4C1D95, #6D28D9)`, padding: '88px 24px', textAlign: 'center' }}>
      <div className="container" style={{ maxWidth: 700 }}>
        <SLabel light center>Services Marketplace</SLabel>
        <h1 className="heading-xl" style={{ color: C.white, margin: '14px 0 22px' }}>
          Post Your Requirement.<br /><span style={{ color: C.gold }}>Qualified Providers Quote.</span>
        </h1>
        <p style={{ fontFamily: F.body, fontSize: 17, color: 'rgba(255,255,255,0.78)', lineHeight: 1.72 }}>
          From chartered accountants to travel agents, surgeons to interior designers — verified service providers compete for your business at your budget.
        </p>
      </div>
    </div>
    <div style={{ background: C.cream, padding: '88px 24px' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <SLabel center>Service Categories</SLabel>
          <STitle center>Every Professional Service<br /><span style={{ color: C.green }}>At Your Price.</span></STitle>
        </div>
        <div className="grid-1-2-3" style={{ marginBottom: 60 }}>
          {[['✈️', 'Travel', 'International Tours · Domestic · Pilgrimage · Honeymoon · Adventure'], ['📊', 'Finance & Tax', 'GST Filing · ITR · Audit · Company Registration · TDS Compliance'], ['🏥', 'Healthcare', 'Surgery Packages · Dental · Orthopaedic · Eye Care · Diagnostics'], ['⚖️', 'Legal', 'Property Docs · Contracts · Litigation · IP Registration · Corporate Law'], ['🏠', 'Home Services', 'Modular Kitchen · Full Interior · Renovation · Architecture · Vastu'], ['📚', 'Education', 'UPSC / IIT Coaching · MBA Prep · Skill Courses · Language Classes'], ['💼', 'Business Services', 'Branding · Digital Marketing · Staffing · IT Support · Import-Export'], ['🛡️', 'Insurance', 'Health · Life · Vehicle · Business · Term Plans — Quotes at Your Budget']].map(([e, t, d]) => (
            <div key={t} style={{ background: C.white, borderRadius: 18, padding: 28, border: `1.5px solid ${C.border}` }}>
              <div style={{ fontSize: 36, marginBottom: 14 }}>{e}</div>
              <h3 style={{ fontFamily: F.body, fontSize: 17, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>{t}</h3>
              <p style={{ fontFamily: F.body, fontSize: 13, color: C.muted, lineHeight: 1.65 }}>{d}</p>
            </div>
          ))}
        </div>
        <div style={{ background: `linear-gradient(135deg, ${C.greenDark}, ${C.green})`, borderRadius: 22, padding: '48px 24px', display: 'grid', gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr', gap: 52, alignItems: 'center' }}>
          <div>
            <SLabel light>For Service Providers</SLabel>
            <h2 className="heading-md" style={{ color: C.white, lineHeight: 1.2, marginBottom: 16 }}>Only Pay When a<br /><span style={{ color: C.gold }}>Qualified Lead Arrives.</span></h2>
            <p style={{ fontFamily: F.body, fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.72, marginBottom: 28 }}>Configure your service type, target buyer budget, and geography. Only matching service requests reach you. Out-of-city leads cost 50% less.</p>
            <Btn variant="gold" size="lg" onClick={() => go('Vendor Signup')}>Register as a Provider →</Btn>
          </div>
          <div style={{ display: 'grid', gap: 14 }}>
            {[['🎓', 'CA / Tax Consultant', 'Set: Finance → Accounting → GST Filing → ₹1,000–₹5,000/month'], ['✈️', 'Travel Agent', 'Set: Travel → International → Europe → ₹80,000–₹3,00,000'], ['🏥', 'Hospital / Clinic', 'Set: Healthcare → Orthopaedic → ₹50,000–₹5,00,000']].map(([e, role, filter]) => (
              <div key={role} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 14, padding: '16px 18px' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 22 }}>{e}</span>
                  <div>
                    <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.white }}>{role}</div>
                    <div style={{ fontFamily: F.body, fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>{filter}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── BLOG PAGE ─────────────────────────────────────────────────────────────────
export const BlogPage = () => {
  const POSTS = [
    { tag: 'Buyer Guide', title: 'How to Get the Best Price on Electronics Without Bargaining', desc: 'Traditional bargaining has limits. Kanjusee removes them entirely — here\'s how buyers across India are getting electronics at their own price.', time: '5 min', date: 'Mar 2025', views: '2.4K' },
    { tag: 'Vendor Tips', title: 'Why Lead-Based Marketing Is Replacing Facebook Ads for Retailers in 2025', desc: 'Facebook ads cost ₹120+ per click with no conversion guarantee. Lead-based marketing flips the equation entirely.', time: '7 min', date: 'Feb 2025', views: '3.1K' },
    { tag: 'Platform', title: 'Understanding the Buyer Seriousness Score and How It Protects Vendors', desc: 'Every buyer on Kanjusee carries a score. Vendors see it. Buyers don\'t. Here\'s the full logic and why it matters for the marketplace.', time: '4 min', date: 'Feb 2025', views: '1.8K' },
    { tag: 'Vendor Tips', title: 'The 48-Hour Repeat Lead Rule: How It Protects Your Wallet', desc: 'If a buyer reposts the same demand type within 48 hours, you get an alert — not a charge. Here\'s the full logic.', time: '5 min', date: 'Jan 2025', views: '1.2K' },
    { tag: 'Market Insights', title: 'Apple vs Samsung: Which Brand Generates More Leads in Gujarat — Q1 2025 Data', desc: 'Analysing real demand data from Kanjusee across 12 major cities in Gujarat. The results might surprise you.', time: '8 min', date: 'Jan 2025', views: '4.6K' },
    { tag: 'Services', title: 'International Tours at Your Budget: How the Service Marketplace Changes Travel Booking', desc: 'The travel industry is notorious for opaque pricing. Kanjusee\'s service marketplace puts the buyer in control from the first click.', time: '6 min', date: 'Dec 2024', views: '2.2K' },
  ];
  return (
    <div style={{ background: C.cream, padding: '88px 24px' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 52 }}>
          <SLabel center>Kanjusee Blog</SLabel>
          <STitle center>Tips, Insights &<br />Market Intelligence</STitle>
          <SSub center>Buyer guides, vendor strategies, and real demand data from India's buyer-powered marketplace.</SSub>
        </div>
        <div className="grid-1-2-3">
          {POSTS.map(p => (
            <div key={p.title} style={{ background: C.white, borderRadius: 18, padding: 26, cursor: 'pointer', border: `1.5px solid ${C.border}`, display: 'flex', flexDirection: 'column' }}>
              <Tag sx={{ marginBottom: 16 }}>{p.tag}</Tag>
              <h3 style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal, lineHeight: 1.55, marginBottom: 12, flex: 1 }}>{p.title}</h3>
              <p style={{ fontFamily: F.body, fontSize: 13, color: C.muted, lineHeight: 1.65, marginBottom: 18 }}>{p.desc}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: F.body, fontSize: 12, color: C.muted, flexWrap: 'wrap', gap: 8 }}>
                <span>⏱ {p.time} read · {p.date}</span>
                <span>👁 {p.views}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── ABOUT PAGE ────────────────────────────────────────────────────────────────
export const AboutPage = ({ go }) => (
  <div>
    <div style={{ background: `linear-gradient(140deg, ${C.greenDark}, ${C.green})`, padding: '88px 24px', textAlign: 'center' }}>
      <div className="container" style={{ maxWidth: 680 }}>
        <SLabel light center>Our Story</SLabel>
        <h1 className="heading-xl" style={{ color: C.white, margin: '14px 0 22px' }}>
          Built in Surat.<br /><span style={{ color: C.gold }}>For Every Buyer in India.</span>
        </h1>
        <p style={{ fontFamily: F.body, fontSize: 17, color: 'rgba(255,255,255,0.78)', lineHeight: 1.72 }}>
          Kanjusee was born from one frustration: why does the buyer always have to accept the seller's price? We flipped the model entirely.
        </p>
      </div>
    </div>
    <div style={{ background: C.white, padding: '88px 24px' }}>
      <div className="container" style={{ maxWidth: 1000 }}>
        <div className="grid-1-2" style={{ gap: 64, marginBottom: 64 }}>
          <div>
            <SLabel>The Problem</SLabel>
            <h2 className="heading-md" style={{ color: C.charcoal, marginBottom: 16 }}>Traditional Commerce Is Broken for Buyers</h2>
            <p style={{ fontFamily: F.body, fontSize: 15, color: C.muted, lineHeight: 1.75 }}>You browse 10 websites, call 5 dealers, and still end up paying 20–30% more than you should. Geographic limitations mean your local vendor charges far more than one 200km away. Kanjusee collapses that gap — for every product and service, in every city.</p>
          </div>
          <div>
            <SLabel>The Imaginations</SLabel>
            <h2 className="heading-md" style={{ color: C.charcoal, marginBottom: 16 }}>A Venture Studio from Surat</h2>
            <p style={{ fontFamily: F.body, fontSize: 15, color: C.muted, lineHeight: 1.75 }}>Kanjusee is a venture by The Imaginations — a Surat-based studio building platforms that solve real problems in Indian markets. Our portfolio includes Vakalat.com (legal tech), Legured (citizen legal security score), Rajkaran.com (governance intelligence), and more.</p>
          </div>
        </div>
        <div style={{ background: C.cream, borderRadius: 22, padding: '48px 24px', textAlign: 'center', marginBottom: 52 }}>
          <SLabel center>Our Mission</SLabel>
          <p style={{ fontFamily: F.display, fontSize: 26, color: C.green, fontStyle: 'italic', lineHeight: 1.55, maxWidth: 680, margin: '0 auto 28px' }}>
            "To give every Indian buyer the power to set the price — and every Indian vendor a guaranteed, pre-qualified lead for every rupee spent."
          </p>
          <Btn size="lg" onClick={() => go('Contact')}>Partner with Us →</Btn>
        </div>
        <div className="grid-1-2-4" style={{ gap: 20 }}>
          {[['2022', 'Founded in Surat by The Imaginations'], ['180+', 'Cities across India'], ['3', 'Marketplace models in one platform'], ['₹0', 'Cost to post a demand for buyers']].map(([v, l]) => (
            <div key={l} style={{ textAlign: 'center', padding: 24, border: `1.5px solid ${C.border}`, borderRadius: 16 }}>
              <div style={{ fontFamily: F.display, fontSize: 32, fontWeight: 700, color: C.green, marginBottom: 8 }}>{v}</div>
              <div style={{ fontFamily: F.body, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ── CONTACT PAGE ──────────────────────────────────────────────────────────────
export const ContactPage = () => {
  const [f, setF] = useState({ name: '', email: '', phone: '', type: '', msg: '' });
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  if (sent) return (
    <div style={{ background: C.cream, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 72, marginBottom: 20 }}>✅</div>
        <h2 style={{ fontFamily: F.display, fontSize: 36, color: C.green, marginBottom: 10 }}>Message Sent!</h2>
        <p style={{ fontFamily: F.body, fontSize: 16, color: C.muted }}>We'll get back to you within 24 hours.</p>
      </div>
    </div>
  );

  return (
    <div style={{ background: C.cream, padding: '88px 24px' }}>
      <div className="container" style={{ maxWidth: 700 }}>
        <SLabel center>Get in Touch</SLabel>
        <STitle center>Partner with Kanjusee</STitle>
        <SSub center>Vendor onboarding, brand partnerships, press, or investor relations — we'd love to hear from you.</SSub>
        <div style={{ background: C.white, borderRadius: 22, padding: '38px 24px', border: `1.5px solid ${C.border}` }}>
          <div className="grid-1-2" style={{ gap: 12 }}>
            <input style={inp()} placeholder="Full Name" value={f.name} onChange={e => set('name', e.target.value)} />
            <input style={inp()} placeholder="Email Address" value={f.email} onChange={e => set('email', e.target.value)} />
          </div>
          <input style={inp()} placeholder="Phone Number" value={f.phone} onChange={e => set('phone', e.target.value)} />
          <select style={inp({ cursor: 'pointer' })} value={f.type} onChange={e => set('type', e.target.value)}>
            <option value="">Type of Inquiry</option>
            <option>Vendor Onboarding</option><option>Brand Partnership</option>
            <option>Press / Media</option><option>Investor Relations</option><option>General</option>
          </select>
          <textarea style={inp({ resize: 'vertical', marginBottom: 24 })} rows={5} placeholder="Your message..." value={f.msg} onChange={e => set('msg', e.target.value)} />
          <Btn size="lg" full onClick={() => setSent(true)}>Send Message →</Btn>
        </div>
        <div className="grid-1-2-3" style={{ gap: 14, marginTop: 22 }}>
          {[['📧', 'info@kanjusee.com'], ['📞', '+91 8000223315'], ['📍', 'Vesu, Surat (Guj) 395009']].map(([i, v]) => (
            <div key={v} style={{ background: C.white, borderRadius: 12, padding: '18px 14px', textAlign: 'center', border: `1.5px solid ${C.border}` }}>
              <div style={{ fontSize: 26, marginBottom: 8 }}>{i}</div>
              <div style={{ fontFamily: F.body, fontSize: 12, color: C.muted }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── VENDOR SIGNUP PAGE ────────────────────────────────────────────────────────
export const VendorSignupPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // Form is controlled end-to-end so step-1 entries carry through to the
  // real /auth/register call at the end of step 3.
  const [form, setForm] = useState({
    firmName: '', fullName: '', mobileNumber: '', email: '',
    password: '', confirmPassword: '',
    vendorType: 'normal', gstin: '',
  });
  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [pickedState, setPickedState] = useState('');
  const [pickedCity, setPickedCity] = useState('');

  const [otp, setOtp] = useState('');
  const [registrationToken, setRegistrationToken] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');

  useEffect(() => {
    import('../services/api').then(m => m.locationAPI.states())
      .then(r => setAllStates(r.data.data || []))
      .catch(() => { });
  }, []);

  useEffect(() => {
    setPickedCity('');
    if (!pickedState) { setAllCities([]); return; }
    import('../services/api').then(m => m.locationAPI.cities(pickedState))
      .then(r => setAllCities(r.data.data || []))
      .catch(() => setAllCities([]));
  }, [pickedState]);

  const canAdvanceFromStep = (s) => {
    if (s === 1) {
      return form.firmName && form.fullName && form.mobileNumber && form.email && pickedState && pickedCity;
    }
    return true;
  };

  const handleEmailBlur = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = form.email ? form.email.trim().toLowerCase() : '';
    if (!normalizedEmail) {
      setEmailError('');
      return;
    }
    if (!emailRegex.test(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    try {
      const res = await authAPI.checkEmail(normalizedEmail);
      if (res.data.registered) {
        setEmailError('Email already registered.');
      } else {
        setEmailError('');
      }
    } catch (err) {
      console.error('Failed to check email registration status:', err);
    }
  };

  const handleActivate = async () => {
    setError('');
    if (!form.password || form.password.length < 6) {
      return setError('Please set a password of at least 6 characters.');
    }
    if (form.password !== form.confirmPassword) {
      return setError("Passwords don't match — please re-enter.");
    }
    setSubmitting(true);
    try {
      const res = await authAPI.register({
        fullName: form.fullName,
        email: form.email.trim().toLowerCase(),
        password: form.password,
        mobileNumber: form.mobileNumber,
        role: 'vendor',
        vendorType: form.vendorType,
        city: pickedCity,
        state: pickedState,
      });
      setRegistrationToken(res.data.registrationToken);
      setStep(3);
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Could not create account. Please try again.';
      if (errMsg.includes('Email already registered')) {
        setEmailError('Email already registered.');
        setStep(1);
      } else {
        setError(errMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    if (otp.length !== 6) return setError('Please enter a 6-digit OTP.');
    setSubmitting(true);
    try {
      const res = await authAPI.verifyRegistration({ registrationToken, otp });
      login(res.data.data.token, res.data.data.user);
      const q = new URLSearchParams({
        firmName: form.firmName,
        gstin: form.gstin,
        state: pickedState,
        city: pickedCity,
      }).toString();
      navigate(`/vendor/onboarding?${q}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ background: C.cream, padding: '88px 24px' }}>
      <div className="container" style={{ maxWidth: 620 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <SLabel center>Vendor Registration</SLabel>
          <h1 className="heading-md" style={{ color: C.charcoal, lineHeight: 1.1, marginBottom: 12 }}>Start Receiving<br /><span style={{ color: C.green }}>Qualified Leads Today</span></h1>
          <p style={{ fontFamily: F.body, fontSize: 14, color: C.muted }}>Free to register. Only pay when a matching lead is delivered to your wallet.</p>
        </div>

        {/* Existing vendor? Straight to sign in. */}
        <div style={{ textAlign: 'center', marginBottom: 32, fontSize: 13, color: C.muted, fontFamily: F.body }}>
          Already a vendor?{' '}
          <Link to="/vendor/login" style={{ color: C.green, fontWeight: 700, textDecoration: 'none' }}>Sign in to your dashboard →</Link>
        </div>

        {/* Progress bar */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 40 }}>
          {['Business Info', 'Billing Setup', 'Verify'].map((label, i) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: step > i + 1 ? C.green : step === i + 1 ? C.green : C.border, color: C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body, fontSize: 13, fontWeight: 700 }}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 600, color: step === i + 1 ? C.green : C.muted, marginTop: 6, whiteSpace: 'nowrap' }}>{label}</div>
              </div>
              {i < 2 && <div style={{ flex: 1, height: 2, background: step > i + 1 ? C.green : C.border, margin: '0 8px', marginBottom: 20 }} />}
            </div>
          ))}
        </div>

        <div style={{ background: C.white, borderRadius: 22, padding: '36px 24px', border: `1.5px solid ${C.border}` }}>
          {step === 1 && (
            <div>
              <h3 style={{ fontFamily: F.body, fontSize: 16, fontWeight: 700, color: C.green, marginBottom: 24 }}>Step 1 — Business Information</h3>
              <input style={inp()} placeholder="Business / Shop Name"
                value={form.firmName} onChange={e => setField('firmName', e.target.value)} />
              <div className="grid-1-2" style={{ gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <input style={inp()} placeholder="Owner Full Name"
                    value={form.fullName} onChange={e => setField('fullName', e.target.value)} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <input
                    style={inp(mobileError ? { marginBottom: 4 } : {})}
                    placeholder="Phone Number"
                    maxLength={10}
                    value={form.mobileNumber}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '');
                      setField('mobileNumber', val);
                      if (val && !/^[6-9]/.test(val)) {
                        setMobileError('Invalid Mobile Number');
                      } else if (val && val.length < 10) {
                        setMobileError('Mobile number must be exactly 10 digits.');
                      } else {
                        setMobileError('');
                      }
                    }}
                  />
                  {mobileError && (
                    <div style={{ background: '#FEE2E2', color: '#B91C1C', padding: '6px 10px', borderRadius: 8, fontSize: '12px', marginTop: '4px', marginBottom: '12px', fontWeight: 500, border: '1px solid rgba(185, 28, 28, 0.15)' }}>
                      {mobileError}
                    </div>
                  )}
                </div>
              </div>
              <input
                style={inp(emailError ? { marginBottom: 4 } : {})}
                type="email"
                placeholder="Email Address"
                value={form.email}
                onChange={e => {
                  setField('email', e.target.value);
                  setEmailError('');
                }}
                onBlur={handleEmailBlur}
              />
              {emailError && (
                <div style={{ background: '#FEE2E2', color: '#B91C1C', padding: '6px 10px', borderRadius: 8, fontSize: '12px', marginTop: '4px', marginBottom: '12px', fontWeight: 500, border: '1px solid rgba(185, 28, 28, 0.15)' }}>
                  {emailError}
                </div>
              )}
              <select
                style={inp({ cursor: 'pointer' })}
                value={pickedState}
                onChange={e => setPickedState(e.target.value)}
              >
                <option value="">Select State</option>
                {allStates.map(s => (
                  <option key={s.state || s} value={s.state || s}>{s.state || s}</option>
                ))}
              </select>
              <select
                style={inp({ cursor: pickedState ? 'pointer' : 'not-allowed' })}
                value={pickedCity}
                onChange={e => setPickedCity(e.target.value)}
                disabled={!pickedState}
              >
                <option value="">{pickedState ? 'Select City' : 'Pick a state first'}</option>
                {allCities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              {/* Vendor type — decides which lead pool they join. */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.charcoal, marginBottom: 8 }}>Vendor Type</div>
                <div className="grid-1-2" style={{ gap: 10 }}>
                  {[
                    { v: 'normal', label: '🏪 Normal', sub: 'Items / services' },
                    { v: 'financial', label: '🏦 Financial', sub: 'Bank / NBFC / lender' },
                  ].map(({ v, label, sub }) => {
                    const active = form.vendorType === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setField('vendorType', v)}
                        style={{
                          padding: '12px 10px', borderRadius: 10, cursor: 'pointer',
                          border: `1.5px solid ${active ? C.green : C.border}`,
                          background: active ? C.greenLight : C.white,
                          fontFamily: F.body, fontSize: 13, fontWeight: 700,
                          color: active ? C.green : C.charcoal, textAlign: 'left',
                        }}
                      >
                        <div>{label}</div>
                        <div style={{ fontSize: 11, fontWeight: 500, color: C.muted, marginTop: 2 }}>{sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 style={{ fontFamily: F.body, fontSize: 16, fontWeight: 700, color: C.green, marginBottom: 24 }}>Step 2 — Secure Your Account</h3>

              {/* Sign-in password — captured before account creation so the
                  vendor can log in from day one, including on mobile. */}
              <div style={{ background: C.greenLight, borderLeft: `3px solid ${C.green}`, borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
                <div style={{ fontFamily: F.body, fontSize: 13, fontWeight: 800, color: C.green, marginBottom: 2 }}>🔐 Set your sign-in password</div>
                <div style={{ fontFamily: F.body, fontSize: 12, color: C.muted }}>You'll use this to sign in to the vendor dashboard on web and the Kanjusee Vendor mobile app.</div>
              </div>
              <div className="grid-1-2" style={{ gap: 12 }}>
                <input style={inp()} type="password" placeholder="Password (min 6 chars)"
                  value={form.password} onChange={e => setField('password', e.target.value)} />
                <input style={inp()} type="password" placeholder="Confirm password"
                  value={form.confirmPassword} onChange={e => setField('confirmPassword', e.target.value)} />
              </div>

              <input style={inp()} placeholder="GSTIN (optional — required for B2B invoice)"
                value={form.gstin} onChange={e => setField('gstin', e.target.value.toUpperCase())} />

              <div style={{ background: C.goldLight, borderRadius: 14, padding: '20px 22px', marginBottom: 22 }}>
                <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.charcoal, marginBottom: 6 }}>Wallet Funding (after KYC)</div>
                <p style={{ fontFamily: F.body, fontSize: 13, color: C.muted, marginBottom: 12 }}>Wallet recharge unlocks once your KYC is verified. Minimum ₹500. Balance is deducted per matching lead received.</p>
                <div style={{ fontFamily: F.body, fontSize: 12, color: C.muted }}>Payment via Razorpay or Paytm. No refunds once loaded.</div>
              </div>
              <div style={{ background: C.greenLight, borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
                <div style={{ fontFamily: F.body, fontSize: 13, color: C.green }}>✅ Monthly GST invoice auto-generated on 1st · Emailed + downloadable from dashboard</div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 style={{ fontFamily: F.body, fontSize: 16, fontWeight: 700, color: C.green, marginBottom: 12 }}>Step 3 — Verify Email</h3>
              <p style={{ fontFamily: F.body, fontSize: 14, color: C.muted, marginBottom: 24 }}>We've sent a 6-digit code to <b>{form.email}</b>. Please enter it below.</p>

              <input
                style={inp({ letterSpacing: 8, fontSize: 24, textAlign: 'center', fontWeight: 800 })}
                placeholder="000000"
                maxLength={6}
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              />

              <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13, fontFamily: F.body, color: C.muted }}>
                Didn't get it? <button type="button" onClick={handleActivate} style={{ background: 'none', border: 'none', color: C.green, fontWeight: 700, cursor: 'pointer' }}>Resend Code</button>
              </p>
            </div>
          )}

          {error && (
            <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 8, padding: '10px 14px', fontFamily: F.body, fontSize: 13, marginBottom: 12, marginTop: 12 }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            {(step > 1 && step < 3) && <Btn variant="outline" onClick={() => setStep(s => s - 1)}>← Back</Btn>}
            {step < 3 ? (
              <Btn
                full
                size="lg"
                disabled={submitting}
                onClick={() => {
                  if (step < 2) {
                    if (!canAdvanceFromStep(step)) {
                      setError('Please fill every field before continuing.');
                      return;
                    }
                    // Validate mobile number format
                    const mobileRegex = /^[6-9]\d{9}$/;
                    if (!mobileRegex.test(form.mobileNumber)) {
                      setMobileError('Please enter a valid 10-digit Indian mobile number.');
                      return;
                    }
                    // Validate email format
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(form.email)) {
                      setEmailError('Please enter a valid email address.');
                      return;
                    }
                    if (emailError) {
                      return;
                    }
                    setError('');
                    setStep(s => s + 1);
                    return;
                  }
                  handleActivate();
                }}
              >
                {step === 2
                  ? (submitting ? 'Creating your account…' : '🚀 Create Vendor Account →')
                  : 'Continue →'}
              </Btn>
            ) : (
              <Btn full size="lg" disabled={submitting} onClick={handleVerifyOtp}>
                {submitting ? 'Verifying…' : 'Verify & Complete Registration →'}
              </Btn>
            )}
          </div>

          {step === 2 && (
            <div style={{ marginTop: 10, fontSize: 12, color: C.muted, textAlign: 'center' }}>
              We'll send a verification code to your email, then walk you through profile + KYC.
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ── APP ROOT ──────────────────────────────────────────────────────────────────
// Shared font CSS — imported once at the root so every public route inherits
// the same typography. Kept here (with the rest of the marketing theme) rather
// than in index.css so the marketing-only fonts don't load on dashboards.
export function MarketingFonts() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,900&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
      * { margin: 0; padding: 0; box-sizing: border-box; }
      button { cursor: pointer; }
      input, select, textarea { font-family: inherit; }
      input:focus, select:focus, textarea:focus { border-color: #1B5C3A !important; }
    `}</style>
  );
}

/**
 * Website is now just the homepage. Each marketing sub-page has its own
 * route (/buyers, /vendors, /pricing, …) and is rendered directly via
 * MarketingRoute in App.js — no more ?page= query param, no more
 * page-switcher here.
 */
export default function Website() {
  const navigate = useNavigate();
  const go = (p) => navigate(PAGE_TO_PATH[p] || '/');

  return (
    <div style={{ fontFamily: F.body, background: C.white, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <MarketingFonts />
      <PublicNav />
      <main style={{ flex: 1 }}><HomePage go={go} /></main>
      <PublicFooter />
    </div>
  );
}
